import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Chip,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import jwtDecode from 'jwt-decode';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(2, 3),
    position: 'relative',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#000',
    fontSize: '1rem',
    fontWeight: 500,
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  titleText: {
    fontWeight: 600,
    fontSize: '1.5rem',
    color: '#000',
    textAlign: 'center',
    width: '100%',
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  extractedDataSection: {
    backgroundColor: '#E8F4F8',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    borderRadius: '4px',
  },
  dataRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  dataLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#000',
    minWidth: '120px',
  },
  dataValue: {
    fontSize: '0.875rem',
    color: '#000',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: '#000',
  },
  documentTable: {
    marginBottom: theme.spacing(2),
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderBottom: '1px solid #E0E0E0',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: '1px solid #E0E0E0',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: '#F5F5F5',
    color: '#000',
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorText: {
    color: '#FF0000',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  addRowButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    textTransform: 'none',
    color: '#000',
    border: '1px solid #000',
    borderRadius: '20px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  noteText: {
    fontSize: '0.75rem',
    color: '#666',
    marginBottom: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
  },
  commentsLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
    color: '#000',
  },
  commentsField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      '& textarea.MuiInputBase-input': {
        lineHeight: '20px',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(0),
    },
  },
  commentsHelperText: {
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: '#666',
  },
  sendButton: {
    backgroundColor: '#e36e39',
    color: '#FFFFFF',
    borderRadius: '20px',
    padding: '8px 32px',
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: '#c44d15',
      color: 'white',
      boxShadow: '0 4px 12px rgba(227, 110, 57, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 6px rgba(227, 110, 57, 0.3)',
    },
    '&:disabled': {
      backgroundColor: '#CCCCCC',
      color: '#666666',
      cursor: 'not-allowed',
    },
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2, 3),
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
  },
  selectField: {
    '& .MuiOutlinedInput-root': {
      height: '40px',
    },
  },
}));

const DocumentResubmissionDialog = ({ open, onClose, batchData, reasonForReset, onSuccess }) => {
  const classes = useStyles();
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [comments, setComments] = useState('');
  const [commentsError, setCommentsError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [fetchingDocuments, setFetchingDocuments] = useState(false);

  // Fetch document types from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!open || !batchData?.vendorMosymphonyID || !batchData?.schrockenDeliveryScheduleBatchID) {
        return;
      }

      setFetchingDocuments(true);
      try {
        const payload = {
          vendorMosymphonyID: batchData.vendorMosymphonyID,
          schrockenDeliveryScheduleBatchID: batchData.schrockenDeliveryScheduleBatchID,
        };

        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocuments`,
          payload
        );

        if (response.data?.status && Array.isArray(response.data.data)) {
          setAvailableDocumentTypes(response.data.data);
        } else {
          setAvailableDocumentTypes([]);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setAvailableDocumentTypes([]);
      } finally {
        setFetchingDocuments(false);
      }
    };

    fetchDocuments();
  }, [open, batchData]);

  // Reset selected documents when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDocuments([]);
      setSelectedDocumentType('');
      setComments('');
      setCommentsError('');
      setDocumentError('');
    }
  }, [open]);

  // Show all documents in dropdown, but disable the selected ones

  const handleDocumentTypeSelect = (value) => {
    if (value && !selectedDocuments.includes(value)) {
      setSelectedDocuments([...selectedDocuments, value]);
      setSelectedDocumentType('');
      if (documentError) {
        setDocumentError('');
      }
    }
  };

  const handleRemoveDocument = (documentToRemove) => {
    setSelectedDocuments(selectedDocuments.filter(doc => doc !== documentToRemove));
  };

  const handleCommentsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 255) {
      setComments(value);
      setCommentsError('');
    } else {
      setCommentsError('Comments cannot exceed 255 characters');
    }
  };

  const handleSendRequest = async () => {
    // Clear previous errors
    setCommentsError('');
    setDocumentError('');

    if (comments.length > 255) {
      setCommentsError('Comments cannot exceed 255 characters');
      return;
    }

    // Validate that at least one document is selected
    if (selectedDocuments.length === 0) {
      setDocumentError('Please select at least one document type');
      return;
    }

    setLoading(true);
    try {
      // Prepare document types as array of objects with documentTypeName and resubmitted (always false)
      const documentTypesForResubmission = selectedDocuments.map(doc => ({
        documentTypeName: doc,
        resubmitted: false
      }));

      // Determine documentResubmissionRequested and resubmitDocumentDigitizationRequired
      // based on reasonForReset
      // documentResubmissionRequested = true for BOTH document resubmission options
      // resubmitDocumentDigitizationRequired = true only for "Document Resubmission with data extracted from digitalized document"
      const documentResubmissionRequested = true; // Always true for document resubmission options
      
      const resubmitDocumentDigitizationRequired = 
        reasonForReset === 'Document Resubmission with data extracted from digitalized document';

      // Get collaboratorTypeID from JWT token
      let collaboratorTypeID = 1; // Default fallback
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const tokenData = jwtDecode(token);
          collaboratorTypeID = tokenData.collaborator_type_id || 1;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }

      const requestData = [{
        schrockenDeliveryScheduleBatchID: batchData?.schrockenDeliveryScheduleBatchID || '',
        poNumber: batchData?.poNumber || '',
        batchNumber: batchData?.batchNumber || '',
        documentTypesForResubmission: documentTypesForResubmission,
        comments: comments,
        documentResubmissionRequested: documentResubmissionRequested,
        resubmitDocumentDigitizationRequired: resubmitDocumentDigitizationRequired,
        vendorMosymphonyID: batchData?.vendorMosymphonyID || '',
        collaboratorTypeID: collaboratorTypeID,
        // Note: brandOwnerMosymphonyID, lastUpdatedBy, senderLegalName, receiverLegalName, and roleNames
        // will be added by the middleware/backend
      }];

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateFGBatchForWorkflowReset`,
        requestData
      );

      if (response.data?.status) {
        // Close the main dialog and trigger success dialog in parent
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setCommentsError(response.data?.message || 'Failed to send request. Please try again.');
      }
    } catch (error) {
      console.error('Error sending request to CMO:', error);
      setCommentsError(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Extract data from batchData (passed from ResetBatchDialog, which gets it from fetchAllFGBatches)
  const extractedData = batchData || {
    poNumber: 'NA',
    batchNumber: 'NA',
    cmo: 'NA',
    batchStatus: 'NA',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: '8px',
          maxWidth: '750px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography
          className={classes.cancelButton}
          onClick={onClose}
        >
          Cancel <CloseIcon style={{ fontSize: '24px', marginLeft: '4px', color: '#FF0000' }} />
        </Typography>
        <Typography className={classes.titleText}>
          {reasonForReset === 'Document Resubmission with data extracted from digitalized document' 
            ? (
              <>
                Document Resubmission with data<br />
                extracted from digitalized document
              </>
            )
            : reasonForReset || 'Document Resubmission'
          }
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Extracted Data Section */}
        <div className={classes.extractedDataSection}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>PO Number</Typography>
              <Typography className={classes.dataValue}>{extractedData.poNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>Batch Number</Typography>
              <Typography className={classes.dataValue}>{extractedData.batchNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>CMO</Typography>
              <Typography className={classes.dataValue}>{extractedData.cmo}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>Batch Status</Typography>
              <Typography className={classes.dataValue}>{extractedData.batchStatus}</Typography>
            </Grid>
          </Grid>
        </div>

        {/* Selected Documents as Chips */}
        {selectedDocuments.length > 0 && (
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <Typography className={classes.sectionTitle} style={{ marginTop: 0, marginBottom: '8px' }}>
              Selected Documents:
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedDocuments.map((doc, index) => (
                <Chip
                  key={index}
                  label={doc}
                  onDelete={() => handleRemoveDocument(doc)}
                  style={{
                    backgroundColor: '#E8F4F8',
                    color: '#000',
                    fontWeight: 500,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Document Type Dropdown */}
        <Typography className={classes.sectionTitle} style={{ marginTop: selectedDocuments.length > 0 ? '16px' : '0' }}>
          Document Type
        </Typography>
        <FormControl fullWidth variant="outlined" className={classes.selectField}>
          <Select
            value={selectedDocumentType}
            onChange={(e) => handleDocumentTypeSelect(e.target.value)}
            displayEmpty
            disabled={fetchingDocuments || availableDocumentTypes.length === 0}
          >
            <MenuItem value="" disabled>
              {fetchingDocuments ? 'Loading...' : availableDocumentTypes.length === 0 ? 'No documents to reset for this batch' : 'Select document type'}
            </MenuItem>
            {availableDocumentTypes.map((type, index) => {
              const isSelected = selectedDocuments.includes(type);
              return (
                <MenuItem key={index} value={type} disabled={isSelected}>
                  {type}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        
        {/* No Documents Message */}
        {/* {!fetchingDocuments && availableDocumentTypes.length === 0 && (
          <Typography className={classes.errorText} style={{ marginTop: '8px', marginBottom: '8px', color: '#666' }}>
            No documents to reset for this batch
          </Typography>
        )} */}

        {/* Document Error */}
        {documentError && (
          <Typography className={classes.errorText} style={{ marginTop: '8px', marginBottom: '8px' }}>
            {documentError}
          </Typography>
        )}

        {/* Note */}
        <Typography className={classes.noteText} style={{ marginTop: '16px' }}>
          Note - Re-submission of Post Manufacturing and Pre-Shipment documents is not allowed after IBD/GRN is generated.
        </Typography>

        {/* Comments Section */}
        <Typography className={classes.commentsLabel}>Comments</Typography>
        <div style={{ position: 'relative', marginBottom: commentsError ? '20px' : '0' }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            placeholder="Enter text here"
            value={comments}
            onChange={handleCommentsChange}
            className={classes.commentsField}
            error={!!commentsError}
            helperText={commentsError}
            FormHelperTextProps={{
              style: { margin: 0, marginTop: '4px' }
            }}
          />
          {!commentsError && (
            <Typography 
              style={{ 
                position: 'absolute', 
                right: '14px', 
                bottom: '6px', 
                fontSize: '0.75rem', 
                color: '#666',
                pointerEvents: 'none',
                backgroundColor: 'white',
                padding: '0 2px'
              }}
            >
              {comments.length}/255
            </Typography>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleSendRequest}
          className={classes.sendButton}
          disabled={!!commentsError || !!documentError || comments.length > 255 || loading || selectedDocuments.length === 0}
        >
          {loading ? 'Sending...' : 'Send Request to CMO'}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default DocumentResubmissionDialog;
